import React, { useMemo } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';

import type { KpiSummaryResponse } from '../../services/KpiSummaryService';

import { useKpiSummary } from '../../hooks/use-kpisummary';

import KpiTile from './KpiTile/KpiTile';

import './KpiTiles.css';

// types for KPI data
export interface KpiData {
  id: string;
  title: string;
  value: string | number;
  subtitle: string;
  // TODO: use change/delta in future mvp
  // change: {
  //   value: string;
  //   type: 'positive' | 'negative';
  // };
  icon: IconKind;
}

// Single source of truth for static KPI tile metadata
const KPI_TILE_METADATA = [
  {
    id: 'pending-recommendations',
    title: 'Pending Recommendations',
    icon: 'Time' as const,
    apiKey: 'pendingRecommendations' as const,
  },
  {
    id: 'active-recommended-rules',
    title: 'Active Recommended Rules',
    icon: 'QuickAction' as const,
    apiKey: 'assignedAccess' as const,
  },
  {
    id: 'total-active-rules',
    title: 'Total Active Rules',
    icon: 'Group' as const,
    apiKey: 'activeRules' as const,
  },
  {
    id: 'workforce-time-saved',
    title: 'Workforce Time Saved',
    icon: 'RequestApproval' as const,
    apiKey: 'workforceTimeSaved' as const,
  },
];

/**
 * Helper function to generate workforce time saved subtitle
 */
function getWorkforceTimeSavedSubtitle(workforceData: {
  label?: string | null;
}): string {
  const NA_TEXT = 'N/A';
  const NA_SUBTITLE_TEXT = `${NA_TEXT} hours of total workforce time saved`;

  const value = workforceData.label ?? NA_TEXT;
  const numValue = Number(value);

  // if not a valid number OR if it's 0, show N/A
  if (Number.isNaN(numValue) || numValue === 0) {
    return NA_SUBTITLE_TEXT;
  }

  return `${value} hours of total workforce time saved`;
}

/**
 * Helper function to generate pending recommendations subtitle
 */
function getPendingRecsSubtitle(pendingData: {
  label?: string | null;
}): string {
  const count = Number(pendingData.label) || 0;

  return `Covers ${count} assigned access`;
}

/**
 * Helper function to generate total active rules subtitle
 */
function getTotalActiveRulesSubtitle(activeRulesData: {
  label?: string | null;
}): string {
  const count = Number(activeRulesData.label) || 0;

  return `${count} Assigned access from these Rules`;
}

/**
 * Helper function to generate assigned access subtitle
 */
function getAssignedAccessSubtitle(assignedAccessData: {
  label?: string | null;
}): string {
  const count = Number(assignedAccessData.label) || 0;

  return `${count} Assigned access from these Rules`;
}

/**
 * Transform API response to KpiData format using metadata configuration
 */
function transformKpiData(apiData: KpiSummaryResponse): KpiData[] {
  const { summary } = apiData;

  return KPI_TILE_METADATA.map((metadata) => {
    let subtitle: string;

    if (metadata.apiKey === 'workforceTimeSaved') {
      subtitle = getWorkforceTimeSavedSubtitle(summary[metadata.apiKey]);
    } else if (metadata.apiKey === 'pendingRecommendations') {
      subtitle = getPendingRecsSubtitle(summary[metadata.apiKey]);
    } else if (metadata.apiKey === 'activeRules') {
      subtitle = getTotalActiveRulesSubtitle(summary[metadata.apiKey]);
    } else {
      subtitle = getAssignedAccessSubtitle(summary[metadata.apiKey]);
    }

    return {
      ...metadata, // Static metadata: id, title, icon
      value: summary[metadata.apiKey].count,
      subtitle,
      change: {
        value: summary[metadata.apiKey].delta, // Delta included for future use
        type: 'positive',
      },
    };
  });
}

function KpiTiles() {
  const { data: apiData, isLoading, error } = useKpiSummary();

  // Determine which tiles to render based on loading state and data availability
  const tilesToRender = useMemo(() => {
    if (isLoading) {
      return KPI_TILE_METADATA; // Show skeleton tiles using metadata
    }

    // Handle all error cases here
    if (error) {
      return null;
    }

    if (!apiData?.summary) {
      return []; // empty state for no data
    }

    return transformKpiData(apiData);
  }, [apiData, isLoading, error]);

  // handle error state
  if (error) {
    Logger.error('KPI tiles API error', error);

    return (
      <Box tag='section' className='Rules-KpiTiles Rules-KpiTiles--error'>
        <div>
          <Icon
            kind='AlertWarningFilled'
            size='xLarge'
            color='--color-background-warning-bold'
          />
        </div>
        <Typography
          kind='body2-bold'
          color='--color-foreground-neutral-bold'
          className='Dashboard-filteredZeroRulesMessage'>
          Unable to load KPI data
        </Typography>
      </Box>
    );
  }

  return (
    <Box tag='section' className='Rules-KpiTiles'>
      {tilesToRender?.map((tile) => (
        <KpiTile key={tile.id} data={tile} isLoading={isLoading} />
      ))}
    </Box>
  );
}

export default KpiTiles;
